<template>
  <ESnippetBase
    class="images-snippet"
    :title="$t('web.snippet.images.title')"
    data-test-id="images-snippet"
  >
    <ul
      class="images-snippet__list"
      :style="listStyle"
      :aria-label="$t('web.snippet.images.title')"
    >
      <li
        v-for="(item, itemIndex) in items"
        :key="`images-snippet-item-${itemIndex}`"
        :style="itemStyle(item)"
        class="images-snippet__item snippet__item"
      >
        <a
          :href="getImageUrl(item)"
          :target="linkTarget"
          :title="item.title"
          class="images-snippet__card"
          data-test-id="images-snippet-card"
          rel="noopener"
        >
          <EThumbnail
            :src="item.thumbnailUrl"
            :alt="item.title"
            class="images-snippet__image"
            fallback="trees"
          />
        </a>
      </li>
    </ul>
    <template #footer>
      <EButton
        as="a"
        variant="outline"
        :href="moreUrl"
        icon="images"
        size="s"
        class="images-snippet__more-link"
        color="result"
      >
        {{ $t('web.snippet.images.more') }}
      </EButton>
    </template>
  </ESnippetBase>
</template>

<script>
import { mapGetters } from 'vuex';

import { isPositiveNumber } from '~~/common/js/universal/validators';
import { getImageUrlHash } from '~~/search/common/js/universal/hash-params';
import EButton from '~~/common/vue/components/button';
import EThumbnail from '~~/common/vue/components/thumbnail';
import ESnippetBase from '~~/search/common/vue/components/snippets/base';
import { IMAGES_SNIPPET_PER_PAGE_DEFAULT } from '~/constants';

const IMAGES_PATH = '/images';

export default {
  name: 'EImagesSnippet',
  components: {
    EButton,
    ESnippetBase,
    EThumbnail,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    perPage: {
      type: Number,
      default: IMAGES_SNIPPET_PER_PAGE_DEFAULT,
      validator: isPositiveNumber,
    },
  },
  computed: {
    ...mapGetters('user', ['linkTarget']),
    moreUrl() {
      const { href } = this.$router.resolve({
        path: IMAGES_PATH,
        query: { q: this.$route.query.q?.toString() },
      });
      return href;
    },
    listStyle() {
      // allows the list to grow inside the carousel,
      // so that only perPage items are initially visible
      const multiplier = this.items.length / this.perPage;
      return `--image-snippet-list-multiplier: ${multiplier}`;
    },
  },
  methods: {
    getImageUrl({ id }) {
      const hash = getImageUrlHash(id);
      const query = { q: this.$route.query.q?.toString() };
      return this.$router.resolve({ path: IMAGES_PATH, hash, query }).href;
    },
    itemStyle({ width, height }) {
      return `--image-snippet-item-width: ${width}; --image-snippet-item-height: ${height};`;
    },
  },
};
</script>

<style lang="scss" scoped>
.images-snippet__list {
  display: flex;
  list-style-type: none;
  margin: 0;
  padding: 0;
  // scale the list to match items per page and adjust for padding
  width: calc(var(--image-snippet-list-multiplier) * 100% + #{$space-1s * 2});
}

.images-snippet__item {
  flex-basis: 0%;
  flex-shrink: 1;
  flex-grow: calc(var(--image-snippet-item-width) / var(--image-snippet-item-height));
  padding: $space-1s #{$space-2s / 2};
  // we have to give the images some height before they're loaded
  // otherwise the IntersectionObserver won't detect them and they won't preload
  min-height: $space-3l;
  scroll-snap-align: start;

  @include desktop {
    &:first-of-type {
      padding-left: $space-2s;
    }
  }
}

.images-snippet__card {
  align-items: center;
  background: var(--color-background-secondary);
  display: flex;
  height: 100%;
  justify-content: center;
  overflow: hidden;
}

.images-snippet__image {
  color: transparent;
  display: block;
  transition: transform $timing-1s $easing;
  transform: scale($scale-2l);
  width: 100%;

  .images-snippet__card:hover & {
    transform: scale($scale-m);
  }

  .images-snippet__card:focus & {
    outline: solid ($border-width * 2) var(--color-focused);
  }
}

.images-snippet__missing-image {
  display: none;

  &:first-child {
    display: unset;
  }
}

.images-snippet__image[lazy="error"] {
  display: none;

  & + .images-snippet__missing-image {
    display: unset;
  }
}
</style>
